import { useEffect, useMemo, useState } from 'react';
import { ImageIcon, Loader2, Plus, Trash2, X } from 'lucide-react';
import type { ProductData } from '../../../types/onboarding';
import { uploadImagesToS3 } from '../../../services/s3/s3Service';
import { toBase64 } from '../../../utils/utils';
import {
  buildOnboardingVariantDraft,
  buildOnboardingVariants,
  createOnboardingVariantGroup,
  getOnboardingVariantCombinations,
  type OnboardingVariantGroup,
  type OnboardingVariantRow,
} from '../../../utils/onboardingVariants';

type ProductVariantsConfiguratorProps = {
  productData: ProductData;
  productImages: string[];
  basePrice: number;
  currencySymbol: string;
  currencyCode: string;
  onChange: (data: Partial<ProductData>) => void;
  onClose: () => void;
};

export function ProductVariantsConfigurator({
  productData,
  productImages,
  basePrice,
  currencySymbol,
  currencyCode,
  onChange,
  onClose,
}: ProductVariantsConfiguratorProps) {
  const [groups, setGroups] = useState<OnboardingVariantGroup[]>(
    () => {
      const draft = buildOnboardingVariantDraft(productData.variants, basePrice);
      return draft.groups.length > 0 ? draft.groups : [createOnboardingVariantGroup(0)];
    }
  );
  const [rows, setRows] = useState<Record<string, OnboardingVariantRow>>(
    () => buildOnboardingVariantDraft(productData.variants, basePrice).rows
  );
  const [uploadingOptionId, setUploadingOptionId] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const productImagesKey = productImages.filter(Boolean).join('|');
  const stableProductImages = useMemo(
    () => productImages.filter(Boolean),
    [productImagesKey]
  );
  const combinations = useMemo(() => getOnboardingVariantCombinations(groups), [groups]);
  const activeGroupsCount = groups.filter((group) =>
    group.title.trim() && group.options.some((option) => option.label.trim())
  ).length;

  useEffect(() => {
    const variants = buildOnboardingVariants({
      groups,
      rows,
      productName: productData.name,
      basePrice,
      productImages: stableProductImages,
      providerId: productData.providerId,
    });

    onChange({ variants });
  }, [basePrice, groups, onChange, productData.name, productData.providerId, rows, stableProductImages]);

  const addGroup = () => {
    if (groups.length >= 2) return;
    setGroups((currentGroups) => [
      ...currentGroups,
      createOnboardingVariantGroup(currentGroups.length),
    ]);
  };

  const updateGroupTitle = (groupId: string, title: string) => {
    setGroups((currentGroups) =>
      currentGroups.map((group) =>
        group.id === groupId ? { ...group, title } : group
      )
    );
  };

  const removeGroup = (groupId: string) => {
    setGroups((currentGroups) => currentGroups.filter((group) => group.id !== groupId));
  };

  const addOption = (groupId: string) => {
    setGroups((currentGroups) =>
      currentGroups.map((group) =>
        group.id === groupId
          ? {
              ...group,
              options: [
                ...group.options,
                { id: `${group.id}-option-${Date.now()}`, label: '' },
              ],
            }
          : group
      )
    );
  };

  const updateOptionLabel = (groupId: string, optionId: string, label: string) => {
    setGroups((currentGroups) =>
      currentGroups.map((group) =>
        group.id === groupId
          ? {
              ...group,
              options: group.options.map((option) =>
                option.id === optionId ? { ...option, label } : option
              ),
            }
          : group
      )
    );
  };

  const updateOptionImageUrl = (groupId: string, optionId: string, imageUrl?: string) => {
    setGroups((currentGroups) =>
      currentGroups.map((group) =>
        group.id === groupId
          ? {
              ...group,
              options: group.options.map((option) =>
                option.id === optionId ? { ...option, imageUrl } : option
              ),
            }
          : group
      )
    );
  };

  const handleOptionImageUpload = async (
    groupId: string,
    optionId: string,
    file?: File
  ) => {
    if (!file) return;
    setUploadingOptionId(optionId);
    setUploadError(null);

    try {
      const fileBase64 = await toBase64(file);
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]+/g, '-').slice(-80);
      const response = await uploadImagesToS3({
        file: fileBase64,
        folder: 'onboarding-variant-options',
        filename: `variant-option-${Date.now()}-${safeName || 'image.webp'}`,
      });
      const imageUrl = response?.s3_url ?? response?.url ?? response;
      if (!imageUrl || typeof imageUrl !== 'string') {
        throw new Error('missing image url');
      }
      updateOptionImageUrl(groupId, optionId, imageUrl);
    } catch (error) {
      console.error('Error uploading variant option image', error);
      setUploadError('No pudimos subir la imagen de la variación.');
    } finally {
      setUploadingOptionId(null);
    }
  };

  const removeOption = (groupId: string, optionId: string) => {
    setGroups((currentGroups) =>
      currentGroups.map((group) =>
        group.id === groupId
          ? { ...group, options: group.options.filter((option) => option.id !== optionId) }
          : group
      )
    );
  };

  const getRow = (combination: { key: string; label: string }) =>
    rows[combination.key] ?? {
      key: combination.key,
      label: combination.label,
      price: basePrice > 0 ? String(basePrice) : '',
      available: true,
    };

  const updateRow = (
    combination: { key: string; label: string },
    updates: Partial<OnboardingVariantRow>
  ) => {
    setRows((currentRows) => {
      const currentRow = currentRows[combination.key] ?? {
        key: combination.key,
        label: combination.label,
        price: basePrice > 0 ? String(basePrice) : '',
        available: true,
      };
      return {
        ...currentRows,
        [combination.key]: {
          ...currentRow,
          ...updates,
          key: combination.key,
          label: combination.label,
        },
      };
    });
  };

  const normalizePriceInput = (value: string) =>
    value.replace(',', '.').replace(/[^\d.]/g, '');

  return (
    <div className="rounded-[18px] border border-[#DDE2F1] bg-white px-4 py-4 shadow-[0_16px_42px_rgba(30,41,59,0.12)]">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-extrabold text-[#1E293B]">Variaciones del producto</h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[#64748B] transition-colors hover:bg-[#F1F5F9] hover:text-[#1E293B]"
          aria-label="Cerrar variaciones"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-4">
        {groups.map((group, groupIndex) => (
          <div
            key={group.id}
            className="space-y-3 border-t border-[#E2E8F0] pt-4 first:border-t-0 first:pt-0"
          >
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={group.title}
                onChange={(event) => updateGroupTitle(group.id, event.target.value)}
                placeholder={groupIndex === 0 ? 'Color, talla, tamaño, etc.' : 'Talla, material, estilo, etc.'}
                className="h-10 min-w-0 flex-1 rounded-xl border border-[#E2E8F0] px-3 text-sm font-semibold text-[#1E293B] outline-none transition focus:border-[#3B4A9F] focus:ring-1 focus:ring-[#3B4A9F]"
              />
              <button
                type="button"
                onClick={() => removeGroup(group.id)}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-[#94A3B8] transition-colors hover:bg-red-50 hover:text-red-500"
                aria-label="Eliminar grupo"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-2">
              {group.options.map((option, optionIndex) => (
                <div key={option.id} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={option.label}
                    onChange={(event) => updateOptionLabel(group.id, option.id, event.target.value)}
                    placeholder={`Opción ${optionIndex + 1}`}
                    className="h-9 min-w-0 flex-1 rounded-lg border border-[#E2E8F0] px-3 text-sm text-[#334155] outline-none transition focus:border-[#3B4A9F] focus:ring-1 focus:ring-[#3B4A9F]"
                  />
                  <label
                    className="relative inline-flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-lg border border-[#E2E8F0] bg-white text-[#64748B] transition-colors hover:bg-[#F8FAFC] hover:text-[#3B4A9F]"
                    title="Imagen de esta opción"
                  >
                    {uploadingOptionId === option.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : option.imageUrl ? (
                      <img src={option.imageUrl} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <ImageIcon className="h-3.5 w-3.5" />
                    )}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/jpg,image/webp"
                      className="hidden"
                      onChange={(event) => {
                        void handleOptionImageUpload(group.id, option.id, event.target.files?.[0]);
                        event.target.value = '';
                      }}
                    />
                  </label>
                  {option.imageUrl && (
                    <button
                      type="button"
                      onClick={() => updateOptionImageUrl(group.id, option.id, undefined)}
                      className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[#94A3B8] transition-colors hover:bg-[#F8FAFC] hover:text-[#1E293B]"
                      aria-label="Quitar imagen de opción"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => removeOption(group.id, option.id)}
                    className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[#94A3B8] transition-colors hover:bg-[#F8FAFC] hover:text-[#1E293B]"
                    aria-label="Eliminar opción"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={() => addOption(group.id)}
              className="inline-flex h-9 items-center gap-2 rounded-lg px-2 text-xs font-semibold text-[#3B4A9F] transition-colors hover:bg-[#EEF3FF]"
            >
              <Plus className="h-3.5 w-3.5" />
              Agregar opción
            </button>
          </div>
        ))}

        {groups.length < 2 && (
          <button
            type="button"
            onClick={addGroup}
            className="inline-flex h-10 items-center gap-2 rounded-xl bg-[#F8FAFC] px-3 text-sm font-semibold text-[#334155] ring-1 ring-[#E2E8F0] transition-colors hover:bg-[#EEF3FF] hover:text-[#3B4A9F]"
          >
            <Plus className="h-4 w-4" />
            Agregar tipo de variación
          </button>
        )}

        {groups.length === 0 && (
          <p className="rounded-xl bg-[#F8FAFC] px-3 py-3 text-sm text-[#64748B]">
            Agrega un tipo de variación para empezar.
          </p>
        )}

        {groups.length > 0 && combinations.length === 0 && (
          <p className="rounded-xl bg-[#FFF7ED] px-3 py-3 text-sm text-[#9A3412]">
            Escribe al menos una opción para crear variaciones.
          </p>
        )}

        {combinations.length > 0 && (
          <div className="border-t border-[#E2E8F0] pt-4">
            <div className="mb-2 flex items-center justify-between gap-3">
              <span className="text-xs font-extrabold uppercase text-[#64748B]">
                {activeGroupsCount > 1 ? 'Precios por combinación' : 'Precios por opción'}
              </span>
              <span className="text-xs font-semibold text-[#94A3B8]">{combinations.length} variantes</span>
            </div>

            <div className="space-y-2">
              {combinations.map((combination) => {
                const row = getRow(combination);
                return (
                  <div
                    key={combination.key}
                    className="grid grid-cols-[minmax(0,1fr)_136px_88px] items-center gap-2 rounded-xl border border-[#E2E8F0] px-3 py-2"
                  >
                    <span className="min-w-0 truncate text-sm font-medium text-[#1E293B]">
                      {combination.label}
                    </span>
                    <label className="flex h-9 items-center rounded-lg border border-[#E2E8F0] bg-white px-2 focus-within:border-[#3B4A9F] focus-within:ring-1 focus-within:ring-[#3B4A9F]">
                      <span className="shrink-0 text-xs text-[#64748B]">{currencySymbol}</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={row.price}
                        onChange={(event) =>
                          updateRow(combination, { price: normalizePriceInput(event.target.value) })
                        }
                        className="min-w-0 flex-1 bg-transparent px-1 text-right text-sm outline-none"
                      />
                    </label>
                    <label className="flex items-center justify-end gap-2 text-xs font-semibold text-[#64748B]">
                      <input
                        type="checkbox"
                        checked={row.available}
                        onChange={(event) => updateRow(combination, { available: event.target.checked })}
                        className="h-4 w-4 rounded border-[#CBD5E1] text-[#3B4A9F] focus:ring-[#3B4A9F]"
                      />
                      Activa
                    </label>
                  </div>
                );
              })}
            </div>

            <p className="mt-2 text-right text-[11px] font-semibold uppercase text-[#94A3B8]">
              {currencyCode}
            </p>
          </div>
        )}

        {uploadError && (
          <p className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-500">
            {uploadError}
          </p>
        )}
      </div>
    </div>
  );
}
